import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { assertNever } from '@gitbook/util-ts';

export enum FLOW_STEPS {
    CREATE_ORGANIZATION = 'create-organization',
    SITE_ONBOARDING = 'site-onboarding',

    // redirect step
    COMPLETE = 'complete',
}

type StateCreateOrg = {
    step: FLOW_STEPS.CREATE_ORGANIZATION;
};

type StateSiteOnboarding = {
    step: FLOW_STEPS.SITE_ONBOARDING;
    context: {
        // The organization the user asked to create; not created yet, so the site
        // and org are only created once site onboarding is complete.
        pendingOrganization: PendingOrganization;
    };
};

type StateComplete = {
    step: FLOW_STEPS.COMPLETE;
    context: {
        organization: api.Organization;
        siteId: string;
        hasBootstrappedContent?: boolean;
    };
};

export type PendingOrganization = {
    title: string;
    emailDomainEnabled: boolean;
};

type State = StateCreateOrg | StateSiteOnboarding | StateComplete;

type Action =
    | { step: FLOW_STEPS.CREATE_ORGANIZATION }
    | { step: FLOW_STEPS.SITE_ONBOARDING; context: StateSiteOnboarding['context'] }
    | { step: FLOW_STEPS.COMPLETE; context: StateComplete['context'] };

/**
 * Reducer to keep state of the current step in the flow and the needed context for each step
 */
const reducer = (_: State, action: Action): State => {
    const { step } = action;
    switch (step) {
        case FLOW_STEPS.CREATE_ORGANIZATION:
            return {
                step: FLOW_STEPS.CREATE_ORGANIZATION,
            };
        case FLOW_STEPS.SITE_ONBOARDING:
            return {
                step: FLOW_STEPS.SITE_ONBOARDING,
                context: {
                    ...action.context,
                },
            };
        case FLOW_STEPS.COMPLETE:
            return {
                step: FLOW_STEPS.COMPLETE,
                context: {
                    ...action.context,
                },
            };
        default:
            return assertNever(step);
    }
};

/**
 * Keep the state of the flow and initializes the flow
 */
export function useFlowContext(initialState?: State) {
    const initial = initialState ?? ({ step: FLOW_STEPS.CREATE_ORGANIZATION } as State);
    const [state, dispatch] = React.useReducer(reducer, initial);

    return [state, dispatch] as const;
}
